// Validation utility functions for form inputs

/**
 * Validates date format (MM/DD/YYYY)
 */
export const validateDateFormat = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return { valid: false, error: 'Date is required' };
  }

  // Check MM/DD/YYYY format
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (!dateRegex?.test(dateString)) {
    return { valid: false, error: 'Date must be in MM/DD/YYYY format' };
  }

  // Validate actual date
  const [month, day, year] = dateString?.split('/')?.map(Number);
  const date = new Date(year, month - 1, day);
  
  if (date?.getMonth() !== month - 1 || date?.getDate() !== day || date?.getFullYear() !== year) {
    return { valid: false, error: 'Invalid date' };
  }

  // Check if date is in the past
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  if (date < today) {
    return { valid: false, error: 'Date cannot be in the past' };
  }

  return { valid: true, error: null };
};

/**
 * Validates time format (HH:MM AM/PM)
 */
export const validateTimeFormat = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return { valid: false, error: 'Time is required' };
  }

  // Check HH:MM AM/PM format
  const timeRegex = /^(0?[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM|am|pm)$/;
  if (!timeRegex?.test(timeString)) {
    return { valid: false, error: 'Time must be in HH:MM AM/PM format' };
  }

  return { valid: true, error: null };
};

/**
 * Validates quiet hours time format (24-hour HH:MM)
 */
export const validateQuietHoursTime = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return { valid: false, error: 'Time is required' };
  }

  // Check HH:MM format (24-hour)
  const timeRegex = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;
  if (!timeRegex?.test(timeString)) {
    return { valid: false, error: 'Time must be in HH:MM format (24-hour)' };
  }

  return { valid: true, error: null };
};

/**
 * Validates quiet hours range
 */
export const validateQuietHoursRange = (startTime, endTime) => {
  const startValidation = validateQuietHoursTime(startTime);
  if (!startValidation?.valid) {
    return { valid: false, error: `Start time: ${startValidation?.error}` };
  }

  const endValidation = validateQuietHoursTime(endTime);
  if (!endValidation?.valid) {
    return { valid: false, error: `End time: ${endValidation?.error}` };
  }

  // Convert to minutes for comparison
  const [startHour, startMin] = startTime?.split(':')?.map(Number);
  const [endHour, endMin] = endTime?.split(':')?.map(Number);
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  // Allow overnight ranges (e.g., 22:00 to 08:00)
  if (startMinutes === endMinutes) {
    return { valid: false, error: 'Start and end times cannot be the same' };
  }

  return { valid: true, error: null };
};

/**
 * Validates field type
 */
export const validateFieldType = (value, expectedType, fieldName) => {
  if (value === null || value === undefined) {
    return { valid: false, error: `${fieldName} is required` };
  }

  const actualType = typeof value;
  
  if (expectedType === 'boolean' && actualType !== 'boolean') {
    return { valid: false, error: `${fieldName} must be a boolean value` };
  }

  if (expectedType === 'string' && actualType !== 'string') {
    return { valid: false, error: `${fieldName} must be a text value` };
  }

  if (expectedType === 'number' && actualType !== 'number') {
    return { valid: false, error: `${fieldName} must be a number` };
  }

  return { valid: true, error: null };
};

/**
 * Validates location name
 */
export const validateLocationName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Location name is required' };
  }

  if (name?.trim()?.length < 2) {
    return { valid: false, error: 'Location name must be at least 2 characters' };
  }

  if (name?.trim()?.length > 100) {
    return { valid: false, error: 'Location name must be less than 100 characters' };
  }

  return { valid: true, error: null };
};

/**
 * Validates location address
 */
export const validateLocationAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return { valid: false, error: 'Location address is required' };
  }

  if (address?.trim()?.length < 5) {
    return { valid: false, error: 'Location address must be at least 5 characters' };
  }

  return { valid: true, error: null };
};
